import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, Camera, X, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import ModelFormSlider from '../components/ModelFormSlider';
import ModelDetailsSlider from '../components/ModelDetailsSlider';
import ModelImportModal from '../components/ModelImportModal';
import { modelService } from '../services/modelService';
import { Model, ModelFilters } from '../types/model';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
  builderId: string;
}

const Models: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [models, setModels] = useState<Model[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filters, setFilters] = useState<ModelFilters>({});
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [modelImages, setModelImages] = useState<Record<string, string>>({});

  // Filter options state
  const [filterOptions, setFilterOptions] = useState({
    collections: [] as string[],
    types: [] as string[],
    views: [] as string[],
    spaces: [] as string[]
  });

  // Loading guards to prevent duplicate API calls
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);
  const isFetchingModels = useRef(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<Model | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editModelId = searchParams.get('edit');
  const viewModelId = searchParams.get('view');

  // Fetch projects only once when workspace changes
  useEffect(() => {
    if (workspaceId && projects.length === 0) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Debounce search query - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== debouncedSearchQuery) {
        setDebouncedSearchQuery(searchQuery);
        // Reset to page 1 when search changes
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchQuery]);

  // Track previous values to determine what changed
  const prevFiltersRef = useRef<ModelFilters>(filters);
  const prevSearchRef = useRef(debouncedSearchQuery);

  // Fetch models when dependencies change
  useEffect(() => {
    if (workspaceId) {
      // Check if filters or search changed
      const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
      const searchChanged = prevSearchRef.current !== debouncedSearchQuery;

      // Update refs
      prevFiltersRef.current = filters;
      prevSearchRef.current = debouncedSearchQuery;

      // Initial load or search/filter change - full loading
      if (models.length === 0 || searchChanged || filtersChanged) {
        fetchModels(false);
      } else {
        // Data refresh (sort, pagination only) - only tbody loading
        fetchModels(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, pagination.limit, debouncedSearchQuery, filters, sortBy, sortOrder]);

  // Fetch filter options only when project filter changes
  useEffect(() => {
    if (workspaceId) {
      fetchFilterOptions(filters.projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, filters.projectId]);

  const fetchProjects = async () => {
    if (!workspaceId || isLoadingProjects) return;

    try {
      setIsLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchModels = async (isDataRefresh: boolean = false) => {
    if (!workspaceId || isFetchingModels.current) return;

    try {
      isFetchingModels.current = true;
      // Use dataLoading for data refreshes (sort, pagination), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await modelService.getAll(workspaceId, {
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearchQuery,
        sortBy,
        sortOrder,
        ...filters
      });

      setModels(response.models);
      setPagination(response.pagination);

      // Extract primary image URLs from the response
      const imageMap: Record<string, string> = {};
      response.models.forEach((model: any) => {
        if (model.primaryImageUrl) {
          imageMap[model._id] = model.primaryImageUrl;
        }
      });
      setModelImages(imageMap);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch models');
      console.error('Error fetching models:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
      isFetchingModels.current = false;
    }
  };

  // Fetch all models for a project to get unique filter values
  const fetchFilterOptions = async (projectId?: string) => {
    if (!workspaceId || isLoadingFilterOptions) return;

    try {
      setIsLoadingFilterOptions(true);
      // Fetch all models for the selected project (or all if no project selected)
      const response = await modelService.getAll(workspaceId, {
        page: 1,
        limit: 10000, // Get all models to extract unique values
        projectId: projectId
      });

      const allModels = response.models;

      // Extract unique values
      const collections = new Set<string>();
      const types = new Set<string>();
      const views = new Set<string>();
      const spaces = new Set<string>();

      allModels.forEach((model: Model) => {
        if (model.modelCollection) collections.add(model.modelCollection);
        if (model.type) types.add(model.type);
        if (model.views) model.views.forEach((view: string) => views.add(view));
        if (model.spaces) model.spaces.forEach((space) => spaces.add(space.name));
      });

      setFilterOptions({
        collections: Array.from(collections).sort(),
        types: Array.from(types).sort(),
        views: Array.from(views).sort(),
        spaces: Array.from(spaces).sort()
      });
    } catch (err: any) {
      console.error('Error fetching filter options:', err);
    } finally {
      setIsLoadingFilterOptions(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (key: keyof ModelFilters, value: any) => {
    // If project filter changes, clear dependent filters
    if (key === 'projectId') {
      setFilters({
        projectId: value || undefined,
        // Clear dependent filters when project changes
        modelCollection: undefined,
        type: undefined
      });
    } else {
      setFilters({ ...filters, [key]: value || undefined });
    }
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
  };


  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const handleCreateModel = () => {
    setSearchParams({ action: 'new' });
  };

  const handleEditModel = (modelId: string) => {
    setSearchParams({ edit: modelId });
  };

  const handleViewModel = (modelId: string) => {
    setSearchParams({ view: modelId });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchModels();
  };

  const handleCloseDetailsSlider = () => {
    setSearchParams({});
  };

  const handleDeleteClick = (model: Model) => {
    setModelToDelete(model);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete || !workspaceId) return;

    try {
      await modelService.delete(workspaceId, modelToDelete._id);
      setShowDeleteModal(false);
      setModelToDelete(null);
      fetchModels();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete model');
      console.error('Error deleting model:', err);
    }
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Models</h2>
                <p className="builders-section-subtitle">
                  Manage model information and specifications
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline btn-md" onClick={handleClearFilters}>
                  Clear
                </button>
                <button className="btn btn-primary btn-md" onClick={handleCreateModel}>
                  + Add New Model
                </button>
                <button className="btn btn-primary btn-md" onClick={() => setShowImportModal(true)}>
                  Import
                </button>
              </div>
            </div>
          </div>

          {/* Card Container for Filters and Table */}
          <div className="builders-card-container">
            {/* Filters */}
            <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
            {/* Row 1 */}
            <select
              className="form-input"
              value={filters.projectId || ''}
              onChange={(e) => handleFilterChange('projectId', e.target.value)}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.modelCollection || ''}
              onChange={(e) => handleFilterChange('modelCollection', e.target.value)}
            >
              <option value="">Select Model Collection</option>
              {filterOptions.collections.map((collection) => (
                <option key={collection} value={collection}>
                  {collection}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.type || ''}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">Select Type</option>
              {filterOptions.types.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.bed || ''}
              onChange={(e) => handleFilterChange('bed', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Beds</option>
              <option value="0">Studio</option>
              <option value="1">1 Bed</option>
              <option value="2">2 Bed</option>
              <option value="3">3 Bed</option>
              <option value="4">4+ Bed</option>
            </select>

            {/* Row 2 */}
            <select
              className="form-input"
              value={filters.bath || ''}
              onChange={(e) => handleFilterChange('bath', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Baths</option>
              <option value="1">1 Bath</option>
              <option value="2">2 Bath</option>
              <option value="3">3 Bath</option>
              <option value="4">4+ Bath</option>
            </select>

            <select
              className="form-input"
              value={filters.ceiling || ''}
              onChange={(e) => handleFilterChange('ceiling', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Ceiling</option>
              <option value="8">8 Ft</option>
              <option value="9">9 Ft</option>
              <option value="10">10 Ft</option>
              <option value="11">11+ Ft</option>
            </select>

            <select
              className="form-input"
              value={filters.den || ''}
              onChange={(e) => handleFilterChange('den', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Dens</option>
              <option value="0">No Den</option>
              <option value="1">1 Den</option>
              <option value="2">2 Den</option>
            </select>

            <select
              className="form-input"
              value={filters.outdoorType || ''}
              onChange={(e) => handleFilterChange('outdoorType', e.target.value)}
            >
              <option value="">Select Outdoor Type</option>
              <option value="Balcony">Balcony</option>
              <option value="Terrace">Terrace</option>
              <option value="Patio">Patio</option>
              <option value="None">None</option>
            </select>

            {/* Row 3 */}
            <select
              className="form-input"
              value={filters.eligibleToBuy || ''}
              onChange={(e) => handleFilterChange('eligibleToBuy', e.target.value)}
            >
              <option value="">Select Eligible To Buy</option>
              <option value="parking">Parking</option>
              <option value="locker">Locker</option>
              <option value="bicycle">Bicycle</option>
            </select>

            <select
              className="form-input"
              value={filters.view || ''}
              onChange={(e) => handleFilterChange('view', e.target.value)}
            >
              <option value="">Select Views</option>
              {filterOptions.views.map((view) => (
                <option key={view} value={view}>
                  {view}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.space || ''}
              onChange={(e) => handleFilterChange('space', e.target.value)}
            >
              <option value="">Select Spaces</option>
              {filterOptions.spaces.map((space) => (
                <option key={space} value={space}>
                  {space}
                </option>
              ))}
            </select>

            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                style={{ paddingRight: searchQuery ? '36px' : '12px', width: '100%' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

            {/* Error Display */}
            {error && (
              <div className="builders-error" style={{ marginBottom: '20px' }}>
                <p>{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="builders-container">
                <div className="builders-loading">
                  <div className="builders-loading-spinner" />
                </div>
              </div>
            ) : (
              <>
                {/* Models Table - Full Width */}
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <div className="data-table-container" style={{ minWidth: '1400px', position: 'relative' }}>
              {/* Loading Overlay */}
              {dataLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}>
                  <div className="contacts-loading-spinner"></div>
                </div>
              )}

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>
                      <button
                        onClick={() => handleSort('projectName')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Project
                        <span className="sort-icon">
                          {getSortIcon('projectName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('collection')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Model Collection
                        <span className="sort-icon">
                          {getSortIcon('collection')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('name')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Model Name
                        <span className="sort-icon">
                          {getSortIcon('name')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('type')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Type
                        <span className="sort-icon">
                          {getSortIcon('type')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('bed')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Beds
                        <span className="sort-icon">
                          {getSortIcon('bed')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('bath')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Baths
                        <span className="sort-icon">
                          {getSortIcon('bath')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('den')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Den
                        <span className="sort-icon">
                          {getSortIcon('den')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('media')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Media
                        <span className="sort-icon">
                          {getSortIcon('media')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('flex')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Flex
                        <span className="sort-icon">
                          {getSortIcon('flex')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('views')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        View
                        <span className="sort-icon">
                          {getSortIcon('views')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('area')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Area(Sq.Ft)
                        <span className="sort-icon">
                          {getSortIcon('area')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('outdoorArea')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Outdoor (Sq.Ft)
                        <span className="sort-icon">
                          {getSortIcon('outdoorArea')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('ceiling')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Ceiling (Ft.)
                        <span className="sort-icon">
                          {getSortIcon('ceiling')}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  {models.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="data-table-empty">
                        No models found. Click "+ Add New Model" to create one.
                      </td>
                    </tr>
                  ) : (
                    models.map((model) => (
                      <tr
                        key={model._id}
                        onClick={() => handleViewModel(model._id)}
                        style={{ cursor: dataLoading ? 'wait' : 'pointer' }}
                      >
                        <td>
                          {modelImages[model._id] ? (
                            <img
                              src={modelImages[model._id]}
                              alt={model.name}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #E5E7EB'
                              }}
                              onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                if (placeholder) placeholder.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            style={{
                              width: '60px',
                              height: '60px',
                              backgroundColor: '#E5E7EB',
                              display: modelImages[model._id] ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px'
                            }}
                          >
                            <Camera size={20} color="#9CA3AF" />
                          </div>
                        </td>
                        <td className="data-table-primary-cell">{model.projectName}</td>
                        <td>{model.modelCollection || '-'}</td>
                        <td className="data-table-primary-cell">{model.name}</td>
                        <td>{model.type || '-'}</td>
                        <td>{model.bed || '-'}</td>
                        <td>{model.bath || '-'}</td>
                        <td>{model.den || '-'}</td>
                        <td>{model.media || '-'}</td>
                        <td>{model.flex || '-'}</td>
                        <td>{model.views?.length || 0}</td>
                        <td>{model.area || '-'}</td>
                        <td>{model.outdoorArea || '-'}</td>
                        <td>{model.ceiling || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
                  </div>
                </div>

            {/* Pagination and Records Per Page */}
            <div className="contacts-pagination-controls">
              {/* Records Per Page Selector */}
              <div className="contacts-records-per-page">
                <span>Show</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total: {pagination.total})</span>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="contacts-pagination-wrapper">
                  <Pagination
                    current={pagination.page}
                    total={pagination.pages}
                    onPageChange={handlePageChange}
                    maxWidth={400}
                  />
                </div>
              )}
            </div>
          </>
            )}
          </div>
        </div>

        {/* Drawer for Create/Edit */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title={editModelId ? 'Edit Model' : 'Add Models'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseDrawer} form="model-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="model-form">
                {editModelId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isDrawerOpen && (
            <ModelFormSlider
              workspaceId={workspaceId}
              modelId={editModelId || undefined}
              onSuccess={handleCloseDrawer}
              onCancel={handleCloseDrawer}
            />
          )}
        </Drawer>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Model</h3>
              <p>Are you sure you want to delete "{modelToDelete?.name}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Model Details Slider */}
        {viewModelId && workspaceId && (
          <ModelDetailsSlider
            workspaceId={workspaceId}
            modelId={viewModelId}
            onClose={handleCloseDetailsSlider}
            onSuccess={fetchModels}
          />
        )}

        {/* Model Import Modal */}
        {showImportModal && workspaceId && filters.projectId && (
          <ModelImportModal
            isOpen={showImportModal}
            workspaceId={workspaceId}
            projectId={filters.projectId}
            builderId={projects.find(p => p._id === filters.projectId)?.builderId || ''}
            onClose={() => setShowImportModal(false)}
            onSuccess={() => {
              setShowImportModal(false);
              fetchModels();
            }}
          />
        )}

        {/* Project Selection Required Modal */}
        {showImportModal && (!filters.projectId) && (
          <div className="modal-overlay" onClick={() => setShowImportModal(false)} style={{ zIndex: 1000 }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>Select Project</h2>
                <button className="modal-close-btn" onClick={() => setShowImportModal(false)}>
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body" style={{ padding: '24px' }}>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                  Please select a project before importing models.
                </p>
                <select
                  className="form-input"
                  value={filters.projectId || ''}
                  onChange={(e) => {
                    handleFilterChange('projectId', e.target.value);
                  }}
                  style={{ width: '100%' }}
                >
                  <option value="">Select a project...</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="btn btn-secondary btn-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    </AppLayout>
  );
};

export default Models;
